/**
 * B-T1 — the shop's UNRESOLVED unattended-operation flags (PAST_EXPECTED_END / OPEN_AT_CLOSE), keyed by operation id.
 *
 * Display data only: the server writes and resolves the flags (flag_open_unattended_operations); nothing here ends,
 * edits or infers anything about an operation. A failed read keeps the last known state (unknown is not "none").
 * Its own realtime channel, so a publication gap on the flags table can never silence the operations channel.
 */
import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { groupUnattendedFlags, type UnattendedFlagKind, type UnattendedFlagRow } from '@/lib/timer/unattendedFlags';

export type UnattendedFlagsByOperationId = Map<string, UnattendedFlagKind[]>;
const EMPTY: UnattendedFlagsByOperationId = new Map();

export async function readUnattendedOperationFlags(shopId: string): Promise<UnattendedFlagsByOperationId | null> {
  try {
    const { data, error } = await supabase
      .from('unattended_operation_flags' as never)
      .select('operation_id, flag, threshold_at')
      .eq('shop_id' as never, shopId as never)
      .is('resolved_at' as never, null as never);
    if (error) {
      console.warn('[useUnattendedOperationFlags] read failed:', error.message);
      return null;
    }
    return groupUnattendedFlags((data ?? []) as unknown as UnattendedFlagRow[]);
  } catch (e) {
    console.warn('[useUnattendedOperationFlags] read failed:', e instanceof Error ? e.message : String(e));
    return null;
  }
}

export function useUnattendedOperationFlags(shopId: string | null | undefined, enabled = true): UnattendedFlagsByOperationId {
  const [byOp, setByOp] = useState<UnattendedFlagsByOperationId>(EMPTY);

  useEffect(() => {
    if (!shopId || !enabled) {
      setByOp(EMPTY);
      return;
    }
    let cancelled = false;
    const load = async () => {
      const next = await readUnattendedOperationFlags(shopId);
      if (!cancelled && next) setByOp(next);
    };
    void load();
    const channel = supabase
      .channel(`b-t1-unattended-flags-${shopId}`)
      .on(
        'postgres_changes' as never,
        { event: '*', schema: 'public', table: 'unattended_operation_flags', filter: `shop_id=eq.${shopId}` } as never,
        () => { void load(); },
      )
      .subscribe();
    const onTicketUpdated = () => { void load(); };
    window.addEventListener('ticket-updated', onTicketUpdated);
    return () => {
      cancelled = true;
      window.removeEventListener('ticket-updated', onTicketUpdated);
      void supabase.removeChannel(channel);
    };
  }, [shopId, enabled]);

  return byOp;
}
